import classNames from 'classnames';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDocumentUrlGenerator } from '../../hooks/useUrlGenerator';
import { formatDuration } from '../../lib/utils';

import { audioPlaybackRates, useAudioPlayer } from '../../contexts/AudioPlayerContext';
import BasicButton from '../buttons/BasicButton';
import PlayButton from '../buttons/PlayButton';
import CloseIcon from '../icons/CloseIcon';
import SkipIcon from '../icons/SkipIcon';
import TimesIcon from '../icons/TimesIcon';
import Picture from './Picture';

import styles from '../../styles/partials/mini-audio-player.module.css';

export interface MiniAudioPlayerProps {
    className?: string | null;
}

function MiniAudioPlayer({ className = null }: MiniAudioPlayerProps) {
    const intl = useIntl();
    const url = useDocumentUrlGenerator();
    const player = useAudioPlayer();
    const {
        document = null,
        pagePlayerVisible = false,
        playing = false,
        loading = false,
        currentTime = 0,
        duration = 0,
        rate = 1,
    } = player || {};
    const visible = document !== null && !pagePlayerVisible;

    // Keeps the end of the page above the player
    useEffect(() => {
        if (typeof window === 'undefined') {
            return () => {};
        }
        window.document.body.classList.toggle(styles.bodyWithPlayer, visible);
        return () => {
            window.document.body.classList.remove(styles.bodyWithPlayer);
        };
    }, [visible]);

    if (player === null || document === null) {
        return null;
    }

    const { title = null, image = null, metadata = null } = document;
    const { collections = [], episode = null } = (metadata || {}) as VideoDocumentMetadata & {
        episode?: string | null;
    };
    const [collection = null] = collections || [];
    const serie = collection?.serie || collection;
    const progress = duration > 0 ? Math.min(currentTime / duration, 1) : 0;

    const onClickRate = () => {
        const index = audioPlaybackRates.indexOf(rate);
        player.setRate(audioPlaybackRates[(index + 1) % audioPlaybackRates.length]);
    };

    return (
        <section
            className={classNames([styles.container, { [styles.visible]: visible }, className])}
            aria-label={intl.formatMessage({
                defaultMessage: 'Lecteur audio',
                description: 'Mini audio player label',
            })}
        >
            <div className={styles.progress}>
                <div className={styles.progressBar} style={{ width: `${progress * 100}%` }} />
            </div>
            <div className={styles.thumbnail}>
                {image !== null ? (
                    <Picture media={image} size="small" className={styles.image} />
                ) : null}
            </div>
            <BasicButton href={url(document)} className={styles.label} clickRef="mini_player">
                <span className={styles.surtitle}>
                    {serie?.title || null}
                    {serie !== null && episode !== null ? ' · ' : null}
                    {episode !== null ? (
                        <FormattedMessage
                            defaultMessage="Ép. {episode}"
                            description="Podcast playlist item"
                            values={{ episode }}
                        />
                    ) : null}
                </span>
                <span className={styles.title}>{title}</span>
            </BasicButton>
            <div className={styles.buttons}>
                <BasicButton
                    className={classNames([styles.button, styles.skip])}
                    onClick={() => player.skip(-15)}
                    aria-label={intl.formatMessage({
                        defaultMessage: 'Reculer de 15 secondes',
                        description: 'Audio player button',
                    })}
                >
                    <SkipIcon seconds={15} className={styles.icon} />
                </BasicButton>
                <PlayButton
                    className={styles.playButton}
                    iconClassName={classNames([styles.playIcon, { [styles.isPlayIcon]: !playing }])}
                    playing={playing}
                    loading={loading}
                    onClick={player.toggle}
                    aria-label={
                        playing
                            ? intl.formatMessage({
                                  defaultMessage: 'Pause',
                                  description: 'Audio player button',
                              })
                            : intl.formatMessage({
                                  defaultMessage: 'Écouter',
                                  description: 'Audio player button',
                              })
                    }
                />
                <BasicButton
                    className={classNames([styles.button, styles.skip])}
                    onClick={() => player.skip(30)}
                    aria-label={intl.formatMessage({
                        defaultMessage: 'Avancer de 30 secondes',
                        description: 'Audio player button',
                    })}
                >
                    <SkipIcon seconds={30} direction="forward" className={styles.icon} />
                </BasicButton>
            </div>
            <div className={styles.times}>
                {formatDuration(currentTime)} / {formatDuration(duration)}
            </div>
            <BasicButton
                className={styles.rateButton}
                onClick={onClickRate}
                aria-label={intl.formatMessage({
                    defaultMessage: 'Vitesse de lecture',
                    description: 'Audio player button',
                })}
            >
                <span className={styles.rateLabel}>{intl.formatNumber(rate)}</span>
                <TimesIcon className={styles.rateIcon} />
            </BasicButton>
            <BasicButton
                className={styles.button}
                onClick={player.close}
                aria-label={intl.formatMessage({
                    defaultMessage: 'Fermer le lecteur',
                    description: 'Audio player button',
                })}
            >
                <CloseIcon className={styles.closeIcon} />
            </BasicButton>
        </section>
    );
}

export default MiniAudioPlayer;
